import { authFetch } from '../components/MedicationExtras';
import { AGENCY_PROVIDER_BLOCK } from './agencyInfo';
import { takeFillMode, takePrintRequest, printBlob, isFlatMode } from './fillable';

const filled = (v) => v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0)
  && !/^not available/i.test(String(v));

// Fill blank keys in generated data from the selected patient's saved chart
export const withChartDefaults = (data = {}, defaults = {}) => {
  const out = { ...data };
  Object.entries(defaults).forEach(([k, v]) => {
    if (!filled(out[k]) && filled(v)) out[k] = v;
  });
  return out;
};

export const cms485Defaults = (ctx = {}) => {
  const c = ctx.chart || {};
  return {
    providerNameAddressPhone: AGENCY_PROVIDER_BLOCK,
    patientName: ctx.patientName,
    patientAddress: [c.address || ctx.address, [c.city || ctx.city, c.state || ctx.state, c.zipCode || ctx.zipCode].filter(Boolean).join(' ')].filter(Boolean).join(', '),
    patientHIClaimNo: c.medicareNumber || c.insuranceMemberId || ctx.insuranceMemberId,
    startOfCareDate: c.startOfCareDate || c.admitDate || ctx.startOfCareDate,
    medicalRecordNumber: c.medicalRecordNumber || ctx.mrn,
    dateOfBirth: c.dateOfBirth || ctx.dateOfBirth,
    sex: c.gender || c.sex || ctx.gender,
    allergies: Array.isArray(c.allergies) ? c.allergies.join(', ') : c.allergies,
    physicianNameAddress: [c.primaryPhysician || ctx.primaryPhysician, c.primaryPhysicianAddress, c.primaryPhysicianPhone, c.primaryPhysicianNPI ? `NPI ${c.primaryPhysicianNPI}` : ''].filter(Boolean).join('\n'),
  };
};

export const fetchCms485Blob = async (data, ctx) => {
  const res = await authFetch('/document-generator/cms485-pdf', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: (() => { const m = takeFillMode(); return JSON.stringify({ data: withChartDefaults(data, cms485Defaults(ctx)), patientId: ctx?.patientId || null, fillable: !isFlatMode(m), blank: m === 'blank' }); })(),
  });
  if (!res.ok) throw new Error(`CMS-485 render failed (${res.status})`);
  return res.blob();
};

export const downloadBlob = (blob, filename) => {
  if (takePrintRequest()) { printBlob(blob); return; }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const savePdfToPackages = async (patientId, blob, filename, description) => {
  const form = new FormData();
  form.append('file', new File([blob], filename, { type: 'application/pdf' }));
  form.append('description', description);
  const res = await authFetch(`/patients/${patientId}/packages`, { method: 'POST', body: form });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || `Upload failed (${res.status})`);
  return data;
};

const FUNC_LABELS = { amputation: 'Amputation', bowelBladder: 'Bowel/Bladder (Incontinence)', contracture: 'Contracture', hearing: 'Hearing',
  paralysis: 'Paralysis', endurance: 'Endurance', ambulation: 'Ambulation', speech: 'Speech', legallyBlind: 'Legally Blind', dyspnea: 'Dyspnea' };
const ACT_LABELS = { completeBedridden: 'Complete Bedrest', bedrestBRP: 'Bedrest BRP', upAsToleratedPartial: 'Up As Tolerated',
  transferBedChair: 'Transfer Bed/Chair', exercisesPrescribed: 'Exercises Prescribed', partialWeightBearing: 'Partial Weight Bearing',
  independentAtHome: 'Independent At Home', crutches: 'Crutches', cane: 'Cane', wheelchair: 'Wheelchair', walker: 'Walker', noRestrictions: 'No Restrictions' };
const MENTAL_LABELS = { oriented: 'Oriented', comatose: 'Comatose', forgetful: 'Forgetful', depressed: 'Depressed',
  disoriented: 'Disoriented', lethargic: 'Lethargic', agitated: 'Agitated' };

const checked = (obj = {}, labels) => [
  ...Object.entries(labels).filter(([k]) => obj[k]).map(([, l]) => l),
  ...(obj.other && obj.otherSpecify ? [obj.otherSpecify] : []),
].join('\n');

// CMS-485 form page (flat fields + checkbox groups) -> CMS-485 renderer fields
export const poc485FormToCms = (f = {}) => ({
  patientHIClaimNo: f.patientHIClaimNo, startOfCareDate: f.startOfCareDate,
  certPeriodFrom: f.certificationPeriodFrom, certPeriodTo: f.certificationPeriodTo,
  medicalRecordNumber: f.medicalRecordNo, providerNumber: f.providerNo,
  providerNameAddressPhone: [f.providerName, [f.providerAddress, f.providerCity, f.providerState, f.providerZip].filter(Boolean).join(', '), f.providerPhone].filter(Boolean).join('\n') || AGENCY_PROVIDER_BLOCK,
  patientName: f.patientName, patientAddress: f.patientCity && (f.patientAddress || '').toLowerCase().includes(f.patientCity.toLowerCase()) ? f.patientAddress
    : [f.patientAddress, [f.patientCity, f.patientState, f.patientZip].filter(Boolean).join(' ')].filter(Boolean).join(', '),
  dateOfBirth: f.dateOfBirth, sex: f.sex, medications: f.medications,
  principalDiagnosis: f.principalDiagnosis, surgicalProcedures: f.surgicalProcedure, otherDiagnoses: f.otherDiagnoses,
  dmeSupplies: f.dmeAndSupplies, safetyMeasures: f.safetyMeasures, nutritionalRequirements: f.nutritionalReq, allergies: f.allergies,
  functionalLimitations: checked(f.functionalLimitations, FUNC_LABELS), activitiesPermitted: checked(f.activitiesPermitted, ACT_LABELS),
  mentalStatus: checked(f.mentalStatus, MENTAL_LABELS), prognosis: f.prognosis,
  ordersForDiscipline: f.ordersForDiscipline, goals: f.goalsRehabDischarge,
  nurseSignatureDate: f.nurseSignatureDate, physicianName: f.physicianName,
  physicianNameAddress: [f.physicianName, f.physicianAddress].filter(Boolean).join('\n'),
});
